import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { useProject } from '@/hooks/useProjects';
import { useAuth } from '@/hooks/useAuth';
import { useTasks } from '@/hooks/useTasks';
import { useDependencies } from '@/hooks/useDependencies';
import { useMilestones } from '@/hooks/useMilestones';
import { useTeamMembers } from '@/hooks/useTeamMembers';
import { TaskList } from '@/components/features/tasks';
import { ProjectTimelineSwimLanes } from '@/components/features/timeline';
import { MilestoneManager } from '@/components/features/milestones';
import { ProjectRiskBadge } from '@/components/features/projects/ProjectRiskBadge';
import { ExportButton } from '@/components/features/export/ExportButton';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { Spinner } from '@/components/ui/Spinner';

/**
 * ProjectDetailPage - Shows project details with task table view
 */
export const ProjectDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const parsedId = id ? parseInt(id, 10) : NaN;
  const projectId = Number.isFinite(parsedId) && parsedId > 0 ? parsedId : 0;

  // State
  const [viewMode, setViewMode] = useState<'table' | 'timeline' | 'milestones'>('table');

  // Fetch data
  const { data: project, isLoading: isLoadingProject, error: projectError } = useProject(projectId);
  const { user } = useAuth();
  const { data: tasks } = useTasks(projectId);
  const { data: dependencies } = useDependencies(projectId);
  const { data: milestones } = useMilestones(projectId);
  const { data: teamMembers } = useTeamMembers();

  /**
   * Handle back navigation
   */
  const handleBack = () => {
    navigate('/');
  };

  // Loading state
  if (isLoadingProject) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600">Loading project...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (projectError || !project) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Alert variant="error">
            <strong>Error:</strong> Failed to load project. The project may not exist or you may not have access to it.
          </Alert>
          <div className="mt-4">
            <Button onClick={handleBack} variant="secondary">
              <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Back button */}
          <div className="mb-4">
            <Button onClick={handleBack} variant="secondary" size="sm">
              <ChevronLeftIcon className="w-4 h-4 mr-2" aria-hidden="true" />
              Back to Dashboard
            </Button>
          </div>

          {/* Project header */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900">{project.name}</h1>
              <div className="mt-3">
                <ProjectRiskBadge projectId={projectId} />
              </div>
            </div>
            <ExportButton
              projects={project ? [project] : []}
              tasks={tasks ?? []}
              teamMembers={teamMembers ?? []}
              dependencies={dependencies ?? []}
              milestones={milestones}
            />
          </div>

          {/* View toggle buttons */}
          <div className="mt-6 flex gap-2">
            <Button
              variant={viewMode === 'table' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setViewMode('table')}
              aria-pressed={viewMode === 'table'}
            >
              Table View
            </Button>
            <Button
              variant={viewMode === 'timeline' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setViewMode('timeline')}
              aria-pressed={viewMode === 'timeline'}
            >
              Timeline View
            </Button>
            <Button
              variant={viewMode === 'milestones' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setViewMode('milestones')}
              aria-pressed={viewMode === 'milestones'}
            >
              Milestones
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {viewMode === 'table' && (
          <TaskList projectId={projectId} />
        )}

        {viewMode === 'timeline' && (
          <ProjectTimelineSwimLanes projectId={projectId} />
        )}

        {viewMode === 'milestones' && (
          user?.id ? (
            <div className="bg-white rounded-lg shadow p-6">
              <MilestoneManager projectId={projectId} userId={user.id} />
            </div>
          ) : (
            <Alert variant="error">
              <strong>Authentication required:</strong> Please sign in to manage milestones.
            </Alert>
          )
        )}
      </div>
    </div>
  );
};

